import { Box, CssBaseline, IconButton, InputAdornment, TextField, Typography, useTheme } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import MatrixRain from "../../components/MatrixRain";
import React, { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import logo from "../../assets/images/ScodixLogo.png";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import api from "../../api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

interface FormValues {
  email: string;
  password: string;
}

const schema = yup
  .object({
    email: yup.string().email("Email must be a valid email").required("Email is a required field"),
    password: yup.string().max(255).required("Password is a required field")
  })
  .required();

const Login = (): JSX.Element => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    resolver: yupResolver(schema)
  });
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const response = await api.auth.login(data);

      sessionStorage.setItem("accessToken", response.accessToken);
      sessionStorage.setItem("user", JSON.stringify(response.user));
      sessionStorage.setItem("userId", response.user.id);
      sessionStorage.setItem("tenantId", response.user.tenantId != null ? String(response.user.tenantId) : "");
      sessionStorage.setItem("isAdmin", String(response.user.isAdmin));
      sessionStorage.setItem("isRfidAllowed", String(response.isRfid));

      localStorage.setItem("authToken", response.accessToken);

      toast.success("Login Success");
      navigate("/");
    } catch (error) {
      const message = error instanceof Error && error.message.trim() !== "" ? error.message : "Wrong credentials provided !";
      toast.error(message);
      localStorage.removeItem("authToken");
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("userId");
      sessionStorage.removeItem("tenantId");
      sessionStorage.removeItem("isAdmin");
      sessionStorage.removeItem("isRfidAllowed");
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#fff" }}>
      <CssBaseline />
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 4, minWidth: 0 }}>
        <Box>
          <img src={logo} alt="Scodix" height={48} />
        </Box>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ width: "100%", maxWidth: 400 }}>
            <Typography variant="h4" fontWeight={700} textAlign="center" sx={{ mb: 5 }}>
              Password Generator
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
              <Typography display="block" sx={{ mb: 1 }}>
                Email
              </Typography>
              <TextField
                error={errors.email !== undefined}
                helperText={errors?.email?.message}
                required
                fullWidth
                autoFocus
                {...register("email")}
              />
              <Typography display="block" sx={{ mt: 2, mb: 1 }}>
                Password
              </Typography>
              <TextField
                error={errors.password !== undefined}
                helperText={errors?.password?.message}
                required
                fullWidth
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                {...register("password")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <LoadingButton type="submit" fullWidth loading={isSubmitting} disabled={isSubmitting} sx={{ mt: 4, py: 1.5 }} variant="contained">
                Login
              </LoadingButton>
            </Box>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
              Version {process.env.REACT_APP_VERSION}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: { xs: "none", md: "block" }, position: "relative", overflow: "hidden" }}>
        <MatrixRain />
        <Typography
          variant="h3"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#c3a05f",
            fontFamily: "'Courier New', monospace",
            textShadow: "0 0 12px #000, 0 0 24px #000, 0 0 12px rgba(195,160,95,0.8)",
            fontWeight: 700,
            lineHeight: 1.2,
            textAlign: "center",
            pointerEvents: "none",
            width: "100%",
            px: 2,
            fontSize: { md: "1.5rem", lg: "2.2rem" },
            wordBreak: "break-word"
          }}>
          Password Generator
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
